import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Info, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

const districtPrices = [
  { name: "Monheim am Rhein", slug: "monheim-am-rhein", type: "Haus / Wohnung", min: 2800, max: 4500, trend: "steigend" },
  { name: "Langenfeld", slug: "langenfeld", type: "Haus / Wohnung", min: 2800, max: 4200, trend: "steigend" },
  { name: "Ratingen", slug: "ratingen", type: "Haus / Wohnung", min: 2500, max: 4200, trend: "steigend" },
  { name: "Haan", slug: "haan", type: "Haus / Wohnung", min: 2500, max: 4000, trend: "steigend" },
  { name: "Hilden", slug: "hilden", type: "Wohnung / Haus", min: 2500, max: 3800, trend: "stabil" },
  { name: "Erkrath", slug: "erkrath", type: "Haus / Wohnung", min: 2200, max: 3800, trend: "stabil" },
  { name: "Mettmann", slug: "mettmann", type: "Haus / Wohnung", min: 2000, max: 3200, trend: "stabil" },
  { name: "Velbert", slug: "velbert", type: "Haus / Wohnung", min: 1800, max: 3000, trend: "stabil" },
  { name: "Heiligenhaus", slug: "heiligenhaus", type: "Haus / Wohnung", min: 1800, max: 2800, trend: "stabil" },
  { name: "Wülfrath", slug: "wuelfrath", type: "Haus / Wohnung", min: 1600, max: 2600, trend: "leicht steigend" },
];

const priceFactors = [
  { title: "Lage in der Stadt", desc: "Mikrolage (Straßenseite, Ausblick, Nähe zu Grünflächen) beeinflusst den Preis signifikant." },
  { title: "Baujahr & Zustand", desc: "Modernisierter Altbau vs. sanierungsbedürftiges Objekt – Preisunterschied bis zu 40%." },
  { title: "Energieeffizienz", desc: "Energieausweis-Klasse A-B erzielt deutliche Aufpreise gegenüber schlechter bewerteten Objekten." },
  { title: "Grundstücksgröße", desc: "Bei Häusern ist die Grundstücksgröße ein wesentlicher Werttreiber." },
  { title: "Wohnfläche & Grundriss", desc: "Effiziente Grundrisse mit guter Raumaufteilung werden höher bewertet." },
  { title: "Marktlage & Nachfrage", desc: "Zinsentwicklung und regionale Nachfrage bestimmen das Preisniveau mit." },
];

const schema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Immobilienpreise im Kreis Mettmann 2025",
  description: "Aktuelle Immobilienpreise im Kreis Mettmann 2025 nach Städten – Haus- und Wohnungspreise, Preistrends.",
  publisher: { "@type": "Organization", name: "BRIMO Immobilien NRW" },
};

export default function ImmobilienpreisePage() {
  useMeta(
    "Immobilienpreise Kreis Mettmann 2025 | Aktuelle Marktdaten | BRIMO",
    "Aktuelle Immobilienpreise Kreis Mettmann 2025 nach Städten. Haus- und Wohnungspreise, Preistrends und kostenlose Immobilienbewertung von BRIMO Immobilien NRW."
  );
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end pb-14" data-testid="preise-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Immobilienpreise Kreis Mettmann 2025"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/92" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <nav className="flex items-center gap-1.5 text-gray-500 text-xs mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#E61D38] transition-colors">Start</Link>
            <ChevronRight size={10} />
            <span className="text-gray-400">Immobilienpreise Kreis Mettmann</span>
          </nav>
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            IMMOBILIENPREISE
            <br />
            <span className="text-[#E61D38]">KREIS METTMANN</span>
            <br />
            2025
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
            Aktuelle Marktpreise für Wohnimmobilien nach Städten – für Verkäufer und Käufer im
            Kreis Mettmann.
          </p>
        </div>
      </section>

      {/* Price Table */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="preise-table">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-3"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                PREISE NACH STÄDTEN
              </h2>
              <div className="flex items-center gap-2 mb-8">
                <Info size={13} className="text-[#E61D38]" />
                <p className="text-gray-500 text-xs">
                  Richtwerte auf Basis aktueller Marktdaten 2025. Individuelle Bewertung empfohlen.
                </p>
              </div>

              {/* Table */}
              <div className="overflow-x-auto mb-12">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-400 text-xs uppercase tracking-wider font-bold">Stadt</th>
                      <th className="text-left py-3 px-4 text-gray-400 text-xs uppercase tracking-wider font-bold">Objekttyp</th>
                      <th className="text-left py-3 px-4 text-gray-400 text-xs uppercase tracking-wider font-bold">Preisspanne</th>
                      <th className="text-left py-3 px-4 text-gray-400 text-xs uppercase tracking-wider font-bold">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {districtPrices.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-b border-white/5 hover:bg-[#262626] transition-colors ${i % 2 === 0 ? "bg-[#1a1a1a]" : "bg-[#0d0d0d]"}`}
                        data-testid={`price-row-${i}`}
                      >
                        <td className="py-4 px-4">
                          <Link
                            to={`/immobilie-verkaufen-${row.slug}`}
                            className="text-white font-bold text-sm hover:text-[#E61D38] transition-colors"
                          >
                            {row.name}
                          </Link>
                        </td>
                        <td className="py-4 px-4 text-gray-400 text-xs">{row.type}</td>
                        <td className="py-4 px-4">
                          <span className="text-white font-bold">
                            {row.min.toLocaleString("de-DE")} – {row.max.toLocaleString("de-DE")} €/m²
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span
                            className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${
                              row.trend === "steigend"
                                ? "bg-[#E61D38]/10 text-[#E61D38]"
                                : row.trend === "leicht steigend"
                                ? "bg-orange-900/20 text-orange-400"
                                : "bg-white/5 text-gray-400"
                            }`}
                          >
                            {row.trend}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Kreis Mettmann Average */}
              <div className="bg-[#E61D38]/10 border border-[#E61D38]/30 p-6 flex items-center gap-5 mb-12">
                <TrendingUp size={24} className="text-[#E61D38] flex-shrink-0" />
                <div>
                  <p className="text-white font-bold text-sm uppercase tracking-wider">
                    Kreis Mettmann Gesamt-Durchschnitt
                  </p>
                  <p className="text-[#E61D38] font-black text-2xl mt-1">
                    ca. 2.200 – 3.600 €/m²
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Alle Objekttypen, Kreis Mettmann gesamt, 2025
                  </p>
                </div>
              </div>

              {/* Price Factors */}
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-8"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                WERTFAKTOREN FÜR IMMOBILIEN IM KREIS METTMANN
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {priceFactors.map((factor, i) => (
                  <div
                    key={i}
                    className="bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/30 transition-colors"
                    data-testid={`price-factor-${i}`}
                  >
                    <h3 className="text-white font-bold text-xs uppercase tracking-wider mb-2">
                      {factor.title}
                    </h3>
                    <p className="text-gray-400 text-xs leading-relaxed">{factor.desc}</p>
                  </div>
                ))}
              </div>

              {/* District links */}
              <div className="mt-12 bg-[#262626] border border-white/5 p-6">
                <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-5">
                  Preise nach Stadt – Details
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {cityConfig.districts.map((d) => (
                    <Link
                      key={d.slug}
                      to={`/immobilie-verkaufen-${d.slug}`}
                      className="flex items-center gap-2 text-gray-400 text-xs hover:text-[#E61D38] transition-colors group"
                      data-testid={`price-district-${d.slug}`}
                    >
                      <ArrowRight size={10} className="text-[#E61D38] group-hover:translate-x-0.5 transition-transform" />
                      {d.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div className="sticky top-24">
                <LeadForm
                  formType="market_value"
                  title="Ihren Marktwert ermitteln"
                  ctaText="Kostenlose Bewertung anfragen"
                  showDistrict={true}
                  showPropertyType={true}
                  pageSource="/immobilienpreise-kreis-mettmann"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Strip */}
      <section className="py-12 bg-[#E61D38]" data-testid="preise-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2
            className="text-xl md:text-2xl font-black text-white uppercase tracking-tight mb-3"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            WAS IST IHRE IMMOBILIE WIRKLICH WERT?
          </h2>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto">
            Tabellenpreise sind Richtwerte. Nur eine individuelle Bewertung zeigt den genauen
            Marktwert Ihrer Immobilie im Kreis Mettmann.
          </p>
          <Link
            to="/marktwertermittlung-kreis-mettmann"
            className="inline-flex items-center gap-2 bg-white text-[#E61D38] px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gray-100 transition-colors"
            data-testid="preise-markttwert-cta"
          >
            Jetzt kostenlos bewerten lassen
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
